"use client";
// BeUI tooltip behavior adapted to Fintheon's shared collision contract.

import {
  cloneElement,
  type FocusEvent,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { cn } from "@/lib/utils";
import { uiLibrarySourceAttrs } from "@/lib/ui-library/ui-library-source-attrs";
import {
  resolveTooltipPosition,
  type TooltipPosition,
  type TooltipSide,
} from "./tooltip-position";

export interface TooltipProps {
  children: ReactElement;
  className?: string;
  content: ReactNode;
  delay?: number;
  side?: TooltipSide;
  wrapperClassName?: string;
}

const GAP = 8;
const WARM_WINDOW_MS = 300;
let lastHiddenAt = 0;

export const FROSTED_TOOLTIP_SURFACE_CLASS =
  "grid min-w-32 items-start gap-1.5 rounded-lg border border-border/50 bg-background/50 px-2.5 py-1.5 text-xs text-foreground shadow-xl backdrop-blur-md";

export function Tooltip({
  children,
  className,
  content,
  delay = 120,
  side = "top",
  wrapperClassName,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canHover = useHoverCapable();
  const id = useId();

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    setPosition(
      resolveTooltipPosition(
        rect,
        { height: panel.offsetHeight || 1, width: panel.offsetWidth || 1 },
        side,
        { height: window.innerHeight, width: window.innerWidth },
        GAP,
      ),
    );
  }, [side]);

  const show = useCallback(() => {
    if (!canHover) return;
    if (timer.current) clearTimeout(timer.current);
    const warm = Date.now() - lastHiddenAt < WARM_WINDOW_MS;
    timer.current = setTimeout(() => setOpen(true), warm ? 0 : delay);
  }, [canHover, delay]);

  const showFromFocus = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (open) lastHiddenAt = Date.now();
    setOpen(false);
    setPosition(null);
  }, [open]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const update = () => place();
    const panel = panelRef.current;
    const observer =
      typeof ResizeObserver === "undefined" || !panel
        ? null
        : new ResizeObserver(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    if (panel) observer?.observe(panel);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      observer?.disconnect();
    };
  }, [open, place]);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!isValidElement(children)) return children;

  const child = children as ReactElement<{
    "aria-describedby"?: string;
    onBlur?: (event: FocusEvent<HTMLElement>) => void;
    onFocus?: (event: FocusEvent<HTMLElement>) => void;
    onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  }>;
  const trigger = cloneElement(child, {
    "aria-describedby": open ? id : undefined,
    onBlur: (event) => {
      child.props.onBlur?.(event);
      hide();
    },
    onFocus: (event) => {
      child.props.onFocus?.(event);
      showFromFocus();
    },
    onMouseEnter: (event) => {
      child.props.onMouseEnter?.(event);
      show();
    },
    onMouseLeave: (event) => {
      child.props.onMouseLeave?.(event);
      hide();
    },
  });

  return (
    <>
      <span
        ref={anchorRef}
        className={cn("relative inline-flex align-middle", wrapperClassName)}
        {...uiLibrarySourceAttrs("beui")}
      >
        {trigger}
      </span>
      {open && typeof document !== "undefined"
        ? createPortal(
            <span
              aria-hidden={position ? undefined : true}
              className="pointer-events-none fixed z-[9999]"
              style={{
                left: position?.left ?? 0,
                top: position?.top ?? 0,
                visibility: position ? "visible" : "hidden",
              }}
            >
              <span
                ref={panelRef}
                id={id}
                role="tooltip"
                className={cn(FROSTED_TOOLTIP_SURFACE_CLASS, className)}
              >
                {content}
              </span>
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
